import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sprout, ArrowRight, Check, Gauge, Swords, LayoutGrid, CalendarDays, Camera,
  TrendingUp, Sparkles, Menu, X, Plug, ShieldCheck, Building2, Star, Zap,
} from "lucide-react";
import Speedometer from "../components/Speedometer";
import HeroHead from "../components/HeroHead";
import { Logo } from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { PLANS } from "../lib/plans";

const reveal = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.16, 1, 0.3, 1] } },
};

function Section({ children, className = "", id }) {
  return (
    <section id={id} className={`relative px-6 sm:px-10 lg:px-16 ${className}`}>
      <div className="max-w-6xl mx-auto relative z-10">{children}</div>
    </section>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [sp] = useSearchParams();
  const variant = Number(sp.get("v")) || 1;
  const authed = user && user !== null;
  const primaryCta = () => navigate(authed ? "/app" : "/login");

  return (
    <div className="min-h-screen relative overflow-x-clip" style={{ background: "#F6F8F1" }}>
      {/* Ambient gradient orbs */}
      <div className="blob" style={{ width: 620, height: 620, top: -180, right: -140, background: "radial-gradient(circle at 30% 30%, rgba(182,225,79,.55), rgba(147,192,32,.12))" }} />
      <div className="blob" style={{ width: 460, height: 460, top: 380, left: -180, background: "radial-gradient(circle at 50% 50%, rgba(147,192,32,.28), transparent 70%)" }} />
      <div className="blob" style={{ width: 520, height: 520, top: 1500, right: -160, background: "radial-gradient(circle at 50% 50%, rgba(182,225,79,.30), transparent 70%)" }} />

      {/* Nav + hard-coloured hero head */}
      <HeroHead variant={variant} authed={authed} onCta={primaryCta} />
      {false && (<>
      <header className="sticky top-0 z-50">
        <div className="px-6 sm:px-10 lg:px-16 mt-4">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl px-4 sm:px-5 py-2.5 flex items-center shadow-[0_14px_36px_rgba(22,24,15,.20)]" style={{ background: "var(--grad-ink)" }}>
            <Link to="/" className="flex items-center" data-testid="landing-logo">
              <Logo height={22} bare />
            </Link>
            <nav className="hidden md:flex items-center gap-7 ml-9 text-sm font-medium text-white/65">
              <a href="#how" className="hover:text-white transition-colors">How it works</a>
              <a href="#pricing" className="hover:text-white transition-colors">The ladder</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            </nav>
            <div className="ml-auto hidden md:flex items-center gap-2">
              <Link to="/login" className="px-3 py-2 rounded-xl text-sm font-medium text-white/80 hover:text-white hover:bg-white/10 transition-all focus-ring" data-testid="landing-signin">Sign in</Link>
              <button onClick={primaryCta} className="btn-primary focus-ring" data-testid="landing-getstarted">{authed ? "Open dashboard" : "Get started"} <ArrowRight size={15} strokeWidth={2} /></button>
            </div>
            <button className="ml-auto md:hidden p-2 rounded-xl text-white hover:bg-white/10 transition-all focus-ring" onClick={() => setOpen((o) => !o)} data-testid="landing-menu">{open ? <X size={20} /> : <Menu size={20} />}</button>
          </div>
          {open && (
            <div className="md:hidden rounded-2xl mt-2 px-5 py-4 space-y-3" style={{ background: "var(--grad-ink)" }}>
              {[["how", "How it works"], ["ladder", "The ladder"], ["pricing", "Pricing"]].map(([h, l]) => <a key={h} href={`#${h}`} onClick={() => setOpen(false)} className="block text-white/80">{l}</a>)}
              <button onClick={primaryCta} className="btn-primary w-full focus-ring">{authed ? "Open dashboard" : "Get started"}</button>
            </div>
          )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <Section className="pt-14 sm:pt-20 pb-24">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <motion.div initial="hidden" animate="show" variants={reveal}>
            <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wider text-green-dark glass" style={{ color: "#5F8213" }}>
              <Building2 size={13} strokeWidth={2.2} /> Built for estate agents
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-ink leading-[1.04] mt-6">
              Stop <span className="gradient-text">guessing</span> with your agency's social.
            </h1>
            <p className="text-lg text-ink-2 mt-6 leading-relaxed max-w-xl">
              Growth measures your brand, shows how you compare with local rivals, and tells you exactly what to post next — then helps you do it, or does it for you.
            </p>
            <div className="flex flex-wrap items-center gap-3 mt-9">
              <button onClick={primaryCta} className="btn-primary focus-ring !px-6 !py-3.5 text-base" data-testid="hero-cta">See your Growth Score <ArrowRight size={17} strokeWidth={2} /></button>
              <a href="#pricing" className="btn-secondary focus-ring !px-6 !py-3.5 text-base">View pricing</a>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-8 text-sm text-ink-2">
              <span className="inline-flex items-center gap-2"><Check size={15} color="#5F8213" strokeWidth={2.5} /> No setup fees</span>
              <span className="inline-flex items-center gap-2"><Check size={15} color="#5F8213" strokeWidth={2.5} /> Works with or without NicheCom</span>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.92, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }} className="relative flex justify-center">
            {/* halo */}
            <div className="absolute inset-0 -m-6 rounded-[2rem]" style={{ background: "radial-gradient(circle at 50% 40%, rgba(147,192,32,.22), transparent 65%)" }} />
            <div className="card p-7 w-full max-w-md relative z-10">
              <div className="flex items-center justify-between mb-1">
                <span className="label-caps">Padstow branch</span>
                <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider" style={{ background: "var(--grad-green-soft)", color: "#5F8213" }}><span className="w-1.5 h-1.5 rounded-full" style={{ background: "#93C020" }} />Growing</span>
              </div>
              <div className="flex justify-center"><Speedometer value={74} delta={6} size={330} /></div>
              <div className="mt-3 grid grid-cols-3 gap-2.5 text-center">
                {[["#3", "Local rank"], ["75%", "Content used"], ["+24%", "Engagement"]].map(([v, l]) => (
                  <div key={l} className="rounded-xl py-3" style={{ background: "#F4F7EC" }}>
                    <div className="text-xl font-extrabold tracking-tighter text-ink">{v}</div>
                    <div className="text-[11px] text-ink-3 mt-0.5">{l}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* floating chips */}
            <motion.div className="absolute -left-4 sm:-left-8 top-10 glass rounded-2xl px-3.5 py-2.5 shadow-[0_10px_30px_rgba(22,24,15,.1)] hidden sm:flex items-center gap-2.5 animate-float z-20">
              <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "var(--grad-green)" }}><Zap size={15} color="#fff" strokeWidth={2} /></span>
              <div><div className="text-xs font-bold text-ink leading-none">+5 score</div><div className="text-[10px] text-ink-3 mt-1">7 assets ready</div></div>
            </motion.div>
            <motion.div className="absolute -right-3 sm:-right-6 bottom-16 glass rounded-2xl px-3.5 py-2.5 shadow-[0_10px_30px_rgba(22,24,15,.1)] hidden sm:flex items-center gap-2.5 animate-float z-20" style={{ animationDelay: "1.5s" }}>
              <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "#16180F" }}><Star size={15} color="#B6E14F" strokeWidth={2} fill="#B6E14F" /></span>
              <div><div className="text-xs font-bold text-ink leading-none">#3 of 12</div><div className="text-[10px] text-ink-3 mt-1">↑ 2 this month</div></div>
            </motion.div>
          </motion.div>
        </div>
      </Section>
      </>)}

      {/* Trust strip */}
      <Section className="pb-16">
        <div className="glass rounded-2xl px-6 py-5">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-2 text-center text-sm text-ink-2">
            <span className="inline-flex items-center gap-2"><ShieldCheck size={16} color="#5F8213" strokeWidth={1.75} /> Backed by <span className="font-semibold text-ink">NicheCom</span> — a major UK estate agency media provider</span>
            <span className="hidden sm:block w-px h-4" style={{ background: "#DADDD0" }} />
            <span className="inline-flex items-center gap-2"><Plug size={16} color="#5F8213" strokeWidth={1.75} /> NicheCom is an optional integration — not a requirement</span>
          </div>
        </div>
      </Section>

      {/* Clarity */}
      <Section className="py-16" id="how">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }} variants={reveal} className="max-w-2xl">
          <span className="label-caps">Only estate agents</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink mt-3">We do one thing, exceptionally well.</h2>
          <p className="text-ink-2 mt-4 leading-relaxed">
            Growth isn't a generic scheduler for everyone. It's built for the specific way estate agencies win instructions — property content, local authority and consistency — because that's where most branches are losing ground.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { t: "Scheduling isn't growth", b: "Posting on autopilot doesn't win instructions. Better content and better decisions do." },
            { t: "You're competing locally", b: "Your rivals are down the road. Growth benchmarks you against your actual local market." },
            { t: "Content is going unused", b: "Professional shoots you've already paid for often never make it to social. That's lost ground." },
          ].map((c, i) => (
            <motion.div key={c.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal} transition={{ delay: i * 0.08 }} className="card card-hover p-7">
              <div className="w-11 h-11 rounded-xl grid place-items-center mb-5" style={{ background: "var(--grad-green-soft)" }}><span className="text-lg font-extrabold gradient-text">{i + 1}</span></div>
              <h3 className="font-bold tracking-tight text-ink text-lg">{c.t}</h3>
              <p className="text-sm text-ink-2 mt-2 leading-relaxed">{c.b}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* The loop */}
      <Section className="py-10">
        <div className="rounded-[2rem] p-10 sm:p-14 relative overflow-hidden" style={{ background: "var(--grad-ink)" }}>
          <div className="blob" style={{ width: 360, height: 360, top: -120, right: -60, background: "radial-gradient(circle, rgba(147,192,32,.45), transparent 70%)" }} />
          <div className="relative z-10 text-center max-w-2xl mx-auto">
            <span className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: "#B6E14F" }}>The Growth loop</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-white mt-3">Measure. Understand. Improve. Publish. Repeat.</h2>
          </div>
          <div className="relative z-10 flex flex-wrap justify-center items-center gap-3 mt-10">
            {["Measure", "Understand", "Improve", "Publish", "Measure again"].map((s, i, arr) => (
              <React.Fragment key={s}>
                <motion.span initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                  className="rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ border: "1px solid rgba(255,255,255,.16)", background: "rgba(255,255,255,.05)" }}>{s}</motion.span>
                {i < arr.length - 1 && <ArrowRight size={16} className="text-white/30" />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </Section>

      {/* Pricing */}
      <Section className="py-16" id="pricing">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-caps">Three levels of growth</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink mt-3">Choose the level that fits you.</h2>
          <p className="text-ink-2 mt-3">Start wherever you are — step up whenever you're ready.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-5 mt-10 items-start">
          {[
            { key: "score", icon: Gauge, head: "Know what to do", body: "See your Growth Score, local ranking and exactly where you're losing ground." },
            { key: "schedule", icon: CalendarDays, head: "Make it easy to do", body: "Turn opportunities into a planned Social Week with captions and an approval workflow." },
            { key: "managed", icon: Sparkles, head: "Do it for me", body: "Growth becomes your social team — publishing, Stories, Reels and ongoing optimisation." },
          ].map((c, i) => {
            const p = PLANS[c.key];
            const featured = c.key === "schedule";
            return (
              <motion.div key={c.key} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal} transition={{ delay: i * 0.08 }}
                data-testid={`pricing-${c.key}`}
                className="card-hover rounded-2xl p-7 relative overflow-hidden flex flex-col"
                style={{ background: "var(--grad-ink)", border: featured ? "1.5px solid #93C020" : "1px solid rgba(255,255,255,.08)", boxShadow: featured ? "0 24px 54px -20px rgba(122,170,28,.55)" : "0 20px 44px -22px rgba(22,24,15,.5)" }}>
                <div className="blob" style={{ width: 220, height: 220, top: -120, right: -80, background: "radial-gradient(circle, rgba(147,192,32,.22), transparent 70%)" }} />
                <div className="relative z-10 flex flex-col flex-1">
                  <div className="flex items-start justify-between">
                    <c.icon size={30} color="#B6E14F" strokeWidth={1.75} />
                    {featured && <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider" style={{ background: "var(--grad-green)", color: "#16180F" }}>Popular</span>}
                  </div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] mt-6" style={{ color: "rgba(255,255,255,.5)" }}>{c.head}</div>
                  <h3 className="text-2xl font-extrabold tracking-tight text-white mt-1">{p.name}</h3>
                  <p className="text-sm mt-3 leading-relaxed" style={{ color: "rgba(255,255,255,.6)" }}>{c.body}</p>
                  <ul className="mt-6 space-y-2.5 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm" style={{ color: "rgba(255,255,255,.8)" }}>
                        <Check size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" color="#B6E14F" /> {f}
                      </li>
                    ))}
                  </ul>
                  <button onClick={primaryCta} data-testid={`pricing-cta-${c.key}`}
                    className="mt-7 focus-ring w-full rounded-full py-3 text-sm font-bold transition-colors"
                    style={featured ? { background: "var(--grad-green)", color: "#16180F" } : { background: "transparent", color: "#B6E14F", border: "1.5px solid rgba(182,225,79,.4)" }}>
                    Get started with {p.name.replace("Growth ", "")}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-ink-2 mt-8"><span className="font-semibold gradient-text">Know what to do · Make it easy · Do it for me.</span></p>
      </Section>

      {/* NicheCom */}
      <Section className="py-16" id="nichecom">
        <div className="card p-8 sm:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal}>
              <span className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wider" style={{ background: "var(--grad-green-soft)", color: "#5F8213" }}><Plug size={13} strokeWidth={2} /> Optional integration</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink mt-4 leading-tight">A unique data advantage — if you want it.</h2>
              <p className="text-ink-2 mt-4 leading-relaxed">
                Growth works brilliantly on its own. But if you're a <span className="font-semibold text-ink">NicheCom</span> client, we connect to your media feed — so Growth knows exactly which properties were photographed, what assets exist, and whether you've actually used them.
              </p>
              <div className="mt-6 space-y-3">
                {["Works fully without NicheCom — connect your channels and go", "NicheCom clients unlock content-utilisation & missed-opportunity detection", "Turn shoots you've already paid for into published posts"].map((t) => (
                  <div key={t} className="flex items-start gap-2.5 text-sm text-ink"><Check size={17} color="#5F8213" strokeWidth={2.5} className="mt-0.5 shrink-0" /> {t}</div>
                ))}
              </div>
              <p className="mt-6 text-lg font-semibold tracking-tight text-ink">NicheCom creates the content. <span className="gradient-text">Growth turns it into results.</span></p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="rounded-2xl p-6" style={{ background: "#F4F7EC" }}>
              <div className="flex items-center gap-2 mb-4"><Camera size={16} strokeWidth={1.5} color="#5F8213" /><span className="label-caps">NicheCom · August</span></div>
              <div className="grid grid-cols-2 gap-3">
                {[["28", "Photographed"], ["21", "Used on social"], ["7", "Not yet used"], ["75%", "Utilisation"]].map(([v, l], idx) => (
                  <div key={l} className="rounded-xl p-4 bg-white" style={{ border: "1px solid var(--line)" }}>
                    <div className="text-3xl font-extrabold tracking-tighter" style={{ color: idx === 3 ? "#5F8213" : "#16180F" }}>{v}</div>
                    <div className="text-xs text-ink-2 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Features */}
      <Section className="py-16">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-caps">Everything you need</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tighter text-ink mt-3">One place to grow your brand.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
          {[
            { icon: Gauge, t: "Growth Score", b: "An explainable score that tells you how your brand is really performing." },
            { icon: Swords, t: "Competitor benchmarking", b: "See your exact position in your local market and where rivals are pulling ahead." },
            { icon: Camera, t: "Content utilisation", b: "Spot professional content that's going unused before it's wasted." },
            { icon: LayoutGrid, t: "Content engine", b: "Turn properties and local moments into ready-to-post captions and hashtags." },
            { icon: CalendarDays, t: "Social Week", b: "A balanced, planned week of content with an approval workflow." },
            { icon: TrendingUp, t: "Monthly reports", b: "Clear reporting that connects every insight to a recommended action." },
          ].map((c, i) => (
            <motion.div key={c.t} initial="hidden" whileInView="show" viewport={{ once: true }} variants={reveal} transition={{ delay: i * 0.05 }} className="card card-hover p-6">
              <span className="grid place-items-center w-11 h-11 rounded-xl mb-4" style={{ background: "var(--grad-green-soft)" }}><c.icon size={20} strokeWidth={1.5} color="#5F8213" /></span>
              <h3 className="font-bold tracking-tight text-ink">{c.t}</h3>
              <p className="text-sm text-ink-2 mt-2 leading-relaxed">{c.b}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="pb-20 pt-6">
        <div className="rounded-[2rem] p-10 sm:p-16 text-center relative overflow-hidden" style={{ background: "var(--grad-ink)" }}>
          <div className="blob" style={{ width: 420, height: 420, top: -140, right: -80, background: "radial-gradient(circle, rgba(147,192,32,.5), transparent 70%)" }} />
          <div className="blob" style={{ width: 320, height: 320, bottom: -120, left: -60, background: "radial-gradient(circle, rgba(182,225,79,.35), transparent 70%)" }} />
          <div className="relative z-10">
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tighter text-white">Without Growth, you're <span className="gradient-text">guessing.</span></h2>
            <p className="text-white/60 mt-4 max-w-lg mx-auto">See your branch's Growth Score in minutes and know exactly what to do next.</p>
            <button onClick={primaryCta} className="btn-primary mt-9 focus-ring !px-7 !py-4 text-base" data-testid="final-cta">See your Growth Score <ArrowRight size={17} strokeWidth={2} /></button>
          </div>
        </div>
      </Section>

      {/* Footer */}
      <footer className="relative z-10 border-t" style={{ borderColor: "var(--line)" }}>
        <Section className="py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Logo height={18} />
              <span className="text-sm text-ink-3 hidden sm:inline">Brand intelligence for estate agents</span>
            </div>
            <p className="text-sm text-ink-3">© {new Date().getFullYear()} Growth. Backed by NicheCom.</p>
          </div>
        </Section>
      </footer>
    </div>
  );
}
